import { ImagesHolder } from "./images_holder.js";
import { ImgID } from "./img_id.js";
import { AvatarUtils } from "../common/avatar_utils.js";

// Which startup images the loading screen does NOT wait for.
//
// Everything tagged MENU/GAME or untagged used to be decoded before the menu:
// ~285 files, 25.8 MB of PNG, ~125 MB once decoded. The groups below are art
// the player cannot see on the menu's first frame: overlays that open after a
// level, after a tap into a feature, or once per event. They load right after
// the critical set, while the loader still waits for the network, and every
// path that can show them early waits for them (see whenReady callers).
//
// Priority: lower loads first. GAME_PRIORITY is what a level needs (goal
// icons of NewGoalOverlay, the result overlay and its tournament block).
const DEFERRED_DIRS: ReadonlyArray<[ string, number ]> = [
  [ "assets/art/ui/tournament/", 1 ],
  [ "assets/art/ui/result_overlay/", 2 ],
  [ "assets/art/ui/tower_fail_dialog/", 2 ],
  [ "assets/art/ui/try_again_challenge_dialog/", 2 ],
  [ "assets/art/ui/prize_shot/", 2 ],
  [ "assets/art/ui/loading_screen/prize_shot_loading", 2 ],
  [ "assets/art/ui/one_plus_one_offer_screen/coins_icon", 2 ],
  [ "assets/art/ui/adventure_info_overlay/", 3 ],
  [ "assets/art/ui/daily_tasks_overlay/", 3 ],
  [ "assets/art/ui/daily_challenge_info_overlay/", 3 ],
  [ "assets/art/ui/key_hunt_dialog/", 3 ],
  [ "assets/art/ui/no_levels_dialog/", 4 ],
  [ "assets/art/avatars/", 4 ],
];

// Tournament art the menu itself draws (the mini progress next to the button
// and the crown shown in the HUD).
const CRITICAL_IDS: ReadonlySet<ImgID> = new Set( [
  ImgID.UI_TournamentMiniPbBg,
  ImgID.UI_TournamentMiniPbBase,
  ImgID.UI_TournamentCrownIcon,
] );

const CONCURRENCY = 4;

export class StartupImagePlan
{
  public static readonly TOURNAMENT_PRIORITY = 1;
  public static readonly GAME_PRIORITY = 2;
  public static readonly EVENT_OVERLAYS_PRIORITY = 3;

  private static own_avatar: ImgID | null = null;
  private static started = false;
  private static ready_upto = 0;
  private static max_priority = Math.max( ...DEFERRED_DIRS.map( ( [ , p ] ) => p ) );
  private static waiters: { priority: number; resolve: () => void }[] = [];

  /** null — the image is part of the critical startup set. */
  public static priorityOf( id: ImgID, url: string ): number | null
  {
    if ( CRITICAL_IDS.has( id ) || id === this.ownAvatar() )
      return null;
    for ( const [ dir, priority ] of DEFERRED_DIRS )
      if ( url.includes( dir ) )
        return priority;
    return null;
  }

  public static isDeferred( id: ImgID, url: string ): boolean
  {
    return this.priorityOf( id, url ) !== null;
  }

  public static isReady( max_priority: number = Infinity ): boolean
  {
    return this.ready_upto >= Math.min( max_priority, this.max_priority );
  }

  public static whenReady( max_priority: number = Infinity ): Promise<void>
  {
    if ( this.isReady( max_priority ) )
      return Promise.resolve();
    this.start();
    return new Promise( resolve => this.waiters.push( { priority: Math.min( max_priority, this.max_priority ), resolve } ) );
  }

  /** Runs fn now if the art is there, else once it is (usually well under a second). */
  public static runWhenReady( fn: () => void, max_priority: number = Infinity ): void
  {
    if ( this.isReady( max_priority ) )
      fn();
    else
      void this.whenReady( max_priority ).then( fn );
  }

  /** Starts loading the deferred groups in priority order; safe to call more than once. */
  public static start(): void
  {
    if ( this.started )
      return;
    this.started = true;
    void this.loadAll();
  }

  private static ownAvatar(): ImgID
  {
    if ( this.own_avatar === null )
    {
      let avatar_id = 8;
      try { avatar_id = Number.parseInt( localStorage.getItem( "player_avatar_id" ) ?? "8", 10 ); }
      catch { /* storage blocked: the default avatar */ }
      this.own_avatar = AvatarUtils.getAvatarImgId( Number.isFinite( avatar_id ) ? avatar_id : 8 );
    }
    return this.own_avatar;
  }

  private static async loadAll(): Promise<void>
  {
    const groups = new Map<number, ImgID[]>();
    for ( const d of ImagesHolder.listDescriptors() )
    {
      const priority = this.priorityOf( d.id, d.url );
      if ( priority === null || ImagesHolder.isReady( d.id ) )
        continue;
      const list = groups.get( priority ) ?? [];
      list.push( d.id );
      groups.set( priority, list );
    }

    for ( let priority = 1; priority <= this.max_priority; priority++ )
    {
      const ids = groups.get( priority ) ?? [];
      let next = 0;
      const worker = async (): Promise<void> =>
      {
        for ( let i = next++; i < ids.length; i = next++ )
        {
          // A failed image is not fatal here: UIImage and the DOM backend
          // fetch it again on their own when it is drawn.
          try { await ImagesHolder.ensure( ids[i] ); }
          catch ( e ) { console.warn( `[StartupImages] deferred image ${ids[i]} failed`, e ); }
        }
      };
      await Promise.all( Array.from( { length: Math.min( CONCURRENCY, Math.max( 1, ids.length ) ) }, worker ) );

      this.ready_upto = priority;
      const due = this.waiters.filter( w => w.priority <= priority );
      this.waiters = this.waiters.filter( w => w.priority > priority );
      for ( const w of due ) w.resolve();
    }
  }
}
